import { Link } from "react-router-dom";
import { useState, useEffect, useRef } from "react";
import { useSearchParams, useNavigate } from 'react-router-dom';
import {
  Container,
  Box,
  Typography,
  Paper,
  Button,
  IconButton,
  Stepper,
  Step,
  StepLabel,
  StepContent,
  Alert
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { Order } from "../types/order";
import { ConferenceGroup } from "../types/conferenceGroup";
import { User } from "../types/user";
import { logout } from "../../lib/auth";
import { isMinLength2, isValidEmail, isValidPhoneNumber } from "../../lib/validate";
import BillingInformation from "./registration/BillingInformation";
import ContactInformation from './registration/ContactInformation';
import RoomSelector, { RoomSelectorRef } from "./registration/RoomSelector";
import RoomOccupants, { RoomOccupantsRef } from "./registration/RoomOccupants";
import { roomSelectorErrors } from "../lib/validate";
import RoomRequirements from "./registration/RoomRequirements";
import HotelSelector from "./registration/HotelSelector";
import HotelPaperwork from "./registration/HotelPaperwork";
import OrderSummary from "./registration/OrderSummary";
import Confirmation from "./registration/Confirmation";
import { APIHOST } from '../common';
import { useAuth } from "../components/auth/AuthContext";
import LiveOrder from "../components/LiveOrder";

const NextStepButton = ({ onClick, disabled = false, children }: { 
  onClick: () => void; 
  disabled?: boolean;
  children?: React.ReactNode;
}) => (
  <Button
    variant="contained"
    color="primary"
    size="small"
    onClick={onClick}
    disabled={disabled}
    endIcon={<ArrowForwardIcon fontSize="small" />}
    sx={{ 
      px: 4, 
      py: 0.5,
      mt: 2
    }}
  >
    {children || 'Next Step'}
  </Button>
);

const PrevStepButton = ({ onClick, disabled = false, children }: { 
  onClick: () => void; 
  disabled?: boolean;
  children?: React.ReactNode;
}) => (
  <Button
    color="primary"
    size="small"
    onClick={onClick}
    disabled={disabled}
    startIcon={<ArrowBackIcon fontSize="small" />}
    sx={{ 
      px: 4, 
      py: 0.5,
      mt: 2
    }}
  >
    {children || 'Back'}
  </Button>
);

export default function RegistrationPage() {
  const [activeStep, setActiveStep] = useState(0);
  const [order, setOrder] = useState<Order>({contactFirstName: '', contactLastName: '', contactEmail: '', contactCell: '', billingCountry: 'US', termsAccepted: false} as Order);
  const [conferenceGroup, setConferenceGroup] = useState<ConferenceGroup | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [orderResponse, setOrderResponse] = useState<any>(null);
  const [user, setUser] = useState<User | null>(null);
  const [authError, setAuthError] = useState('');
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [orderId, setOrderId] = useState<string | null>(null);
  const [orderUpdateCounter, setOrderUpdateCounter] = useState(0);
  const roomSelectorRef = useRef<RoomSelectorRef>(null);
  const roomOccupantsRef = useRef<RoomOccupantsRef>(null);
  const open = Boolean(anchorEl);
  
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const orderIdFromUrl = searchParams.get('orderId') || "";

  const { currentUser,loadingUser } = useAuth();

  useEffect(() => {
    // Check authentication and fetch order
   
   setOrder(prev => ({...prev,
      contactFirstName: currentUser?.firstName || '',
      contactLastName: currentUser?.lastName || '',
      contactEmail: currentUser?.email || ''
    }));

    // Fetch order data
    const fetchOrder = async () => {
      if (loadingUser || !currentUser || !orderIdFromUrl) {
        console.log('User not authenticated, still loading, or no order ID provided');
        return;
      }
      
      try {
        setLoading(true);
        setError('');
        
        const authToken = localStorage.getItem('authToken');
        if (!authToken) {
          throw new Error('No authentication token found');
        }

        // Fetch the order with all needed population
        const response = await fetch(`${APIHOST}/api/orders/${orderIdFromUrl}?populate[]=conference_hotel&populate[]=order_rooms.conference_hotel_room&populate[]=order_rooms.order_room_occupants&populate[]=conference_group&populate[]=conference_group.conference`, {
          headers: {
            'Authorization': `Bearer ${authToken}`
          }
        });

        if (!response.ok) {
          throw new Error('Failed to fetch order data');
        }

        const result = await response.json();
        setOrderId(result.data.documentId);
        setOrder(result.data);
        setConferenceGroup(result.data.conference_group);
        
        console.log('Loaded order:', result.data);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'An error occurred');
        console.error('Error fetching order:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchOrder();
    
  }, [currentUser, loadingUser, orderIdFromUrl]);

  const setOrderProp = (field: string, value: any) => {
    setOrder(prevOrder => ({
      ...prevOrder,
      [field]: value
    }));
  };

  // Validate all contact form fields
  const isContactFormValid = () => {
    return isMinLength2(order?.contactFirstName || '') &&
           isMinLength2(order?.contactLastName || '') &&
           isValidEmail(order?.contactEmail || '') &&
           isValidPhoneNumber(order?.contactCell || '');
  };

  // Validate room requirements form
  const isRoomRequirementsValid = () => {
    const hasRoomSelected = (order?.oneBedRoomsNeeded || 0) > 0 || (order?.twoBedRoomsNeeded || 0) > 0;
    const hasArrivalDate = !!order?.selectedArrivalDate;
    const hasDepartureDate = !!order?.selectedDepartureDate;
    return hasRoomSelected && hasArrivalDate && hasDepartureDate;
  };

  // Basic validation for room selection step
  const isRoomSelectionValid = () => {
    return roomSelectorErrors(order).length === 0;
  };

  // Save or update order
  const saveOrder = async () => {
    const authToken = localStorage.getItem('authToken');
    if (!authToken) return;

    // Exclude id and documentId from order data so Strapi doesn't complain
    const {documentId, id, createdAt, updatedAt, conference_hotel, conference_group, order_rooms, ...orderData} = order;

    try {
      const method = orderId ? 'PUT' : 'POST';
      const url = orderId 
        ? `${APIHOST}/api/orders/${orderId}`
        : `${APIHOST}/api/orders`;
  
      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authToken}`
        },
        body: JSON.stringify({
          data: orderData
        })
      })

      if (!response.ok) {
        throw new Error('Failed to save order');
      }

      const result = await response.json();
      console.log('Order saved', result);
      if (!orderId) {
        setOrderId(result.data.documentId);
      }
      // Trigger LiveOrder refresh after saving
      setOrderUpdateCounter(prev => prev + 1);
      
    } catch (err) {
      console.error('Error saving order:', err, orderData);
      // Don't block progress for order save errors
    }
  };

const getConferenceSteps = (conferenceGroup: ConferenceGroup) => {
  return [
    {
        label: 'Contact',
        content: <ContactInformation order={order} setOrderProp={setOrderProp} />
    },
    {
        label: 'Billing', 
        content: <BillingInformation order={order} setOrderProp={setOrderProp} />
    },
    {
        label: 'Room Requirements',
        content: <RoomRequirements order={order} setOrderProp={setOrderProp} valid={isRoomRequirementsValid()} />
    },
    {
        label: 'Hotel Selection',
        content: <HotelSelector order={order} setOrderProp={setOrderProp}/>
    },
    {
        label: 'Rooms',
        content: <RoomSelector ref={roomSelectorRef} order={order} setOrderProp={setOrderProp} setOrderUpdateCounter={setOrderUpdateCounter} />
    },
    {
        label: 'Occupants',
        content: <RoomOccupants ref={roomOccupantsRef} order={order} />
    },
    {
        label: 'Hotel Paperwork',
        content: <HotelPaperwork order={order} setOrderProp={setOrderProp}  />
    },
    {
        label: 'Review & Confirm',
        content: <OrderSummary order={order} setOrderProp={setOrderProp} />
    },
    {
        label: 'Confirmation',
        content: <Confirmation order={order} orderResponse={orderResponse} />
    }
    ];
  }

  const steps = conferenceGroup ? getConferenceSteps(conferenceGroup) : [];
    

  const handleNextStep = async () => {
    
    if (activeStep < steps.length - 2) {
      // Save room data when leaving room selector step
      if (activeStep === 4) {
        await roomSelectorRef.current?.saveAllRoomData()
  
      } else {
         // Save order after each step
        await saveOrder();
      }
     
      setActiveStep(activeStep + 1);
    } else if (activeStep === steps.length - 2) {
      // Submit the registration before showing confirmation
      try {
        setLoading(true);
        setError('');
        
        const response = await fetch(`${APIHOST}/api/orders/submit?id=${orderId}`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('authToken')}`
          },
        });

        if (!response.ok) {
          throw new Error('Failed to submit order');
        }

        const result = await response.json();
        console.log('Order submitted successfully:', result);
        setOrderResponse(result);
        
        // Move to confirmation step
        setActiveStep(activeStep + 1);
        
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to submit order');
        console.error('Order submission error:', err);
      } finally {
        setLoading(false);
      }
    }
  };

  const handlePreviousStep = () => {
    // Don't allow going back from confirmation step
    if (activeStep > 0 && activeStep < steps.length - 1) {
      setActiveStep(activeStep - 1);
    }
  };



  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = () => {
    handleMenuClose();
    logout();
  };

  if (!conferenceGroup) {
    return <Box sx={{ minHeight: '100vh', py: 4 }}>
      <Container maxWidth="lg">
        <Typography variant="h3" component="h1" fontWeight="bold" sx={{ mb: 2 }}>
          Hotel Registration
        </Typography>
         {loading && (
           <Typography variant="body2" color="primary">
             Loading conference data...
           </Typography>
         )}
         {error && (
           <Typography variant="body2" color="error">
             Error: {error}
           </Typography>
         )}
      </Container>
    </Box>;
  }

  return (
    <Box sx={{ minHeight: '100vh', py: 4 }}>
      <Container maxWidth="lg">
        <Box sx={{ my: 4, position: 'relative' }}>
          <Link to="/">
            <IconButton sx={{ mb: 3, color: 'primary.main' }}>
              <ArrowBackIcon />
              <Typography variant="body2" sx={{ ml: 1 }}>
                Back to Home
              </Typography>
            </IconButton>
          </Link>
          {activeStep < steps.length - 1 && (
            <>
              <Typography variant="h3" component="h1" fontWeight="bold" sx={{ mb: 2 }}>
                Hotel Registration
              </Typography>
              {conferenceGroup && (
                <Typography variant="h6" color="text.secondary">
                  {conferenceGroup.conference.longName}
                </Typography>
              )}
            </>
          )}
          {loading && (
            <Typography variant="body2" color="primary">
              Loading conference data...
            </Typography>
          )}
          {error && (
            <Typography variant="body2" color="error">
              Error: {error}
            </Typography>
          )}
        </Box>

        {authError ? (
          <Paper elevation={1} sx={{ p: 4, textAlign: 'center' }}>
            <Alert severity="warning" sx={{ mb: 3 }}>
              {authError}
            </Alert>
            <Button
              variant="contained"
              onClick={() => navigate('/')}
              size="large"
            >
              Go to Home Page
            </Button>
          </Paper>
        ) : (
          <>
            {/* Show live order status when order exists and not on confirmation step */}
            {order && orderId && activeStep < steps.length - 1 && (
              <LiveOrder orderId={orderId} accessCode={order.accessCode || ''} refreshTrigger={orderUpdateCounter} />
            )}
            
            {activeStep < steps.length - 1 ? (
              <Paper elevation={1} sx={{ p: 3, mb: 4 }}>
                <Stepper activeStep={activeStep} orientation="vertical" sx={{ mb: 2 }}>
                  {steps.map((step, index) => (
                    <Step key={step.label}>
                      <StepLabel>
                        <Typography variant="body2" sx={{ fontWeight: activeStep === index ? 600 : 400 }}>
                          {step.label}
                        </Typography>
                      </StepLabel>
                      <StepContent>
                        {step.content}
                        {activeStep < steps.length - 1 && (
                          <>
                            <PrevStepButton 
                              onClick={handlePreviousStep}
                              disabled={activeStep === 0}
                            />
                            <NextStepButton 
                              onClick={handleNextStep}
                              disabled={loading || 
                                (activeStep === 0 && !isContactFormValid()) ||
                                (activeStep === 2 && !isRoomRequirementsValid()) ||
                                (activeStep === 4 && !isRoomSelectionValid())
                              }
                            >
                              {activeStep === steps.length - 2 ? 'Complete Registration' : 'Next Step'}
                            </NextStepButton>
                          </>
                        )}
                      </StepContent>
                    </Step>
                  ))}
                </Stepper>
              </Paper>
            ) : (
              <Box sx={{ mb: 4 }}>
                {steps && steps[activeStep] && steps[activeStep].content}
              </Box>
            )}
          </>
        )}
      </Container>
    </Box>
  );
}